import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../common/Select";
import { WorkspaceGroup } from "../hooks/useWorkspacesSimple";

interface SimpleOwnershipFilterDropdownProps {
  groups: WorkspaceGroup[];
  className?: string;
  onGroupChange?: (groupName: string) => void;
}

/**
 * A simplified ownership filter dropdown that works with the new WorkspaceGroup structure
 */
const SimpleOwnershipFilterDropdown: React.FC<
  SimpleOwnershipFilterDropdownProps
> = ({ groups, className = "", onGroupChange }) => {
  // Use the first group as the default selected group
  const [selectedGroupValue, setSelectedGroup] = React.useState<string | null>(
    null
  );
  const selectedGroup = selectedGroupValue ?? (groups[0]?.name || "");

  const handleValueChange = (groupName: string) => {
    setSelectedGroup(groupName);

    // Call the callback if provided
    if (onGroupChange) {
      onGroupChange(groupName);
    }
  };

  // Helper function to format group display name
  const getGroupDisplayName = (group: WorkspaceGroup) => {
    const isEmpty = group.workspaces.length === 0;
    return isEmpty ? `${group.name} (empty)` : group.name;
  };

  return (
    <Select value={selectedGroup} onValueChange={handleValueChange}>
      <SelectTrigger
        className={`
          h-[35px] flex items-center justify-start gap-1 text-[15px] font-bold leading-6
          bg-transparent border-0 shadow-none px-0 py-0
          focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0
          cursor-pointer
          [&>svg:last-child]:hidden
          [&>span]:!line-clamp-none
          ${className}
        `}
      >
        <SelectValue />
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            d="M6 9l6 6 6-6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </SelectTrigger>

      <SelectContent className="bg-white shadow-md border border-gray-200 z-[1400]">
        {groups.map((group) => (
          <SelectItem key={group.name} value={group.name} className="text-xs">
            <span
              className={group.workspaces.length === 0 ? "text-gray-500" : ""}
            >
              {getGroupDisplayName(group)}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default SimpleOwnershipFilterDropdown;
